import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  Paper,
  Chip,
  Grid,
} from "@mui/material";
import { CalendarMonth, TrendingUp } from "@mui/icons-material";

const AttendanceCalender = ({ isOpen, handleClose, studentId }) => {
  const [studentProfile, setStudentProfile] = useState(null);
  const [monthsData, setMonthsData] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYearID, setSelectedYearID] = useState(null);
  const [registrationData, setRegistrationData] = useState({});

  useEffect(() => {
    const fetchFinancialYears = async () => {
      try {
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const token = sessionStorage.getItem("token");

        const response = await fetch(
          `${apiUrl}/ClassPromotion/GetFinancialYear`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({}),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch financial years");
        }

        const data = await response.json();
        if (data.data === null && data.msg === "Record Not Found") {
          console.error("Record Not Found");
          return;
        }

        setFinancialYears(data);

        const today = new Date();
        const currentYear = today.getFullYear();
        const nextYear = currentYear + 1;

        const financialYearStart = new Date(currentYear, 3, 1);
        const financialYearEnd = new Date(nextYear, 2, 31);

        let currentSessionString;
        if (today >= financialYearStart && today <= financialYearEnd) {
          currentSessionString = `${currentYear}-${nextYear}`;
        } else {
          currentSessionString = `${currentYear - 1}-${currentYear}`;
        }

        const currentSession = data.find(
          (item) => item.finanacialYear === currentSessionString
        );

        if (currentSession) {
          setSelectedYearID(currentSession.financialYearID);
          setRegistrationData((prevData) => ({
            ...prevData,
            financialYearID: currentSession.financialYearID,
          }));
        }
      } catch (error) {
        console.error("Error fetching financial years:", error);
      }
    };

    fetchFinancialYears();
  }, []);

  useEffect(() => {
    if (!selectedYearID) return;

    const fetchStudentProfile = async () => {
      try {
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const token = sessionStorage.getItem("token");
        const studentID = sessionStorage.getItem("employeeId");

        const response = await fetch(`${apiUrl}/Attendance/StudentProfile`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            studentId: studentID,
            yearId: selectedYearID,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === null && data.msg === "Record Not Found") {
            setMonthsData([]);
            return;
          }
          setStudentProfile(data.objData);
          setMonthsData(data.objattendancelist);
        } else {
          console.error("Failed to fetch student profile");
        }
      } catch (error) {
        console.error("API request error:", error);
      }
    };
    fetchStudentProfile();
  }, [selectedYearID]);

  const getCellColor = (value) => {
    if (value === "P") return "#4caf50";
    if (value === "A") return "#f44336";
    if (value === "H") return "#ff9800";
    return "transparent";
  };

  const totalStats = monthsData.reduce(
    (acc, month) => ({
      present: acc.present + (month.presentCount || 0),
      absent: acc.absent + (month.absentCount || 0),
      holiday: acc.holiday + (month.holidayCount || 0),
    }),
    { present: 0, absent: 0, holiday: 0 }
  );

  const overallPercentage = totalStats.present + totalStats.absent > 0
    ? ((totalStats.present / (totalStats.present + totalStats.absent)) * 100).toFixed(1)
    : 0;

  return (
    <Box sx={{ mt: 4, width: '100%', overflow: 'hidden' }}>
      {/* Header Section */}
      <Box
        sx={{
          background: 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
          borderRadius: 3,
          p: 3,
          mb: 3,
          color: 'white',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CalendarMonth sx={{ fontSize: 40 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Attendance Record
            </Typography>
          </Box>

          <Select
            value={selectedYearID || ""}
            onChange={(e) => setSelectedYearID(e.target.value)}
            displayEmpty
            sx={{
              minWidth: 200,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              color: 'white',
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
            }}
          >
            <MenuItem value="" disabled>
              Select Session
            </MenuItem>
            {financialYears.map((year) => (
              <MenuItem key={year.financialYearID} value={year.financialYearID}>
                {year.finanacialYear}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Overall Stats */}
        {monthsData.length > 0 && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {totalStats.present}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Present Days
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {totalStats.absent}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Absent Days
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {totalStats.holiday}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Holidays
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {overallPercentage}%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Overall
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Daily Attendance Table */}
  <Paper
  elevation={3}
  sx={{
    borderRadius: 1,
    overflow: 'hidden',
    mb: 3,
    width: '100%',
    border: '1px solid #d0d0d0',
  }}
>
  <Box sx={{ overflowX: 'auto' }}>
    <Table
      sx={{
        width: '100%',
        borderCollapse: 'collapse',
        '& .MuiTableCell-root': {
          border: '1px solid #d0d0d0',
          padding: '6px 8px',
          fontSize: '0.85rem',
        },
      }}
    >
      <TableHead>
        <TableRow
          sx={{
            background: 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
            '& .MuiTableCell-root': {
              color: 'white',
              fontWeight: 700,
            },
          }}
        >
          <TableCell sx={{ minWidth: 100, position: 'sticky', left: 0, zIndex: 2 }}>
            Month
          </TableCell>
          {[...Array(31).keys()].map((day) => (
            <TableCell
              key={day + 1}
              align="center"
              sx={{ minWidth: 30, width: 30, position: 'sticky', top: 0, zIndex: 2 }}
            >
              {day + 1}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {monthsData.length > 0 ? (
          monthsData.map((month, index) => (
            <TableRow
              key={month.month}
              sx={{
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                '&:hover': {
                  backgroundColor: 'rgba(200, 230, 255, 0.3)',
                },
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 600,
                  position: 'sticky',
                  left: 0,
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                  zIndex: 1,
                  minWidth: 100,
                }}
              >
                {month.month}
              </TableCell>
              {[...Array(31).keys()].map((day) => {
                const value = month[Object.keys(month)[day + 1]];
                return (
                  <TableCell
                    key={day + 1}
                    align="center"
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                    }}
                  >
                    {value && (
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: getCellColor(value),
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          fontWeight: 600,
                          fontSize: '0.65rem',
                        }}
                      >
                        {value}
                      </Box>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={32}
              align="center"
              sx={{ py: 8, color: '#757575', border: '1px solid #d0d0d0' }}
            >
              <Typography variant="h6">
                No attendance data available
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </Box>
</Paper>


      {/* Monthly Summary */}
      {monthsData.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <TrendingUp sx={{ color: 'white', fontSize: 32 }} />
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
              Monthly Summary
            </Typography>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', minWidth: 100 }}>Month</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.95rem', minWidth: 100 }}>Total Days</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.95rem', minWidth: 100 }}>Present</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.95rem', minWidth: 100 }}>Absent</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.95rem', minWidth: 100 }}>Holiday</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.95rem', minWidth: 120 }}>Attendance %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthsData.map((month, index) => (
                  <TableRow
                    key={month.month}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff',
                      '&:hover': {
                        backgroundColor: 'rgba(69,150,164,0.08)',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{month.month}</TableCell>
                    <TableCell align="center">{month.totalDays}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={month.presentCount}
                        size="small"
                        sx={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={month.absentCount}
                        size="small"
                        sx={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={month.holidayCount}
                        size="small"
                        sx={{
                          backgroundColor: '#ff9800',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${month.attendancePercentage}%`}
                        size="small"
                        sx={{
                          background: 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
                          color: 'white',
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default AttendanceCalender;
